import type {
    SensorGroup,
    YAxes,
} from "@station/domain/models/sensor-group.model";
import { SensorNameEnum } from "@station/domain/models/sensor-name.enum";
import type { Sensor } from "@station/domain/models/sensor.model";

export type RelatedChartType =
    "climateIrrigation" | "climateSoil" | "soilIrrigation";

/**
 * Sensor codes of each variable family combined by the predefined related
 * charts of the station detail view (IOT-1755). The variable set of every
 * family was confirmed with product in the IOT-1755 comments:
 *
 * - climate: ambient temperature (TA_0), relative humidity (HR_0),
 *   saturation deficit (DEF_HIDRICO), dew point (DWP_0) and vapour
 *   pressure deficit (DPV).
 * - irrigation: surface (~15cm, HPR_0) and depth (~30cm, HPR_1)
 *   tensiometers.
 * - soil: address-0 soil probe (STV_*): soil temperature (STV_F), soil
 *   humidity (STV_H), pore water EC (STV_J) and bulk/medium EC (STV_O).
 */
const CLIMATE_SENSOR_CODES: readonly SensorNameEnum[] = [
    SensorNameEnum.TA_0,
    SensorNameEnum.HR_0,
    SensorNameEnum.DEF_HIDRICO,
    SensorNameEnum.DWP_0,
    SensorNameEnum.DPV,
];

const IRRIGATION_SENSOR_CODES: readonly SensorNameEnum[] = [
    SensorNameEnum.HPR_0,
    SensorNameEnum.HPR_1,
];

const SOIL_SENSOR_CODES: readonly SensorNameEnum[] = [
    SensorNameEnum.STV_F,
    SensorNameEnum.STV_H,
    SensorNameEnum.STV_J,
    SensorNameEnum.STV_O,
];

export interface RelatedChartDefinition {
    type: RelatedChartType;
    /**
     * Variable families combined by the chart. Availability is evaluated
     * per family: a chart only makes sense when the station has at least
     * one sensor of every family (IOT-1760).
     */
    sensorFamilies: readonly (readonly SensorNameEnum[])[];
}

export const RELATED_CHART_DEFINITIONS: readonly RelatedChartDefinition[] = [
    {
        type: "climateIrrigation",
        sensorFamilies: [CLIMATE_SENSOR_CODES, IRRIGATION_SENSOR_CODES],
    },
    {
        type: "climateSoil",
        sensorFamilies: [CLIMATE_SENSOR_CODES, SOIL_SENSOR_CODES],
    },
    {
        type: "soilIrrigation",
        sensorFamilies: [SOIL_SENSOR_CODES, IRRIGATION_SENSOR_CODES],
    },
];

export interface RelatedChartSelection {
    sensors: Readonly<Sensor>[];
    yAxes: YAxes[];
}

/**
 * A related chart is only available when the station has at least one
 * graphable sensor of every family the chart correlates; otherwise the
 * chart is hidden and replaced by a missing-data notification (IOT-1760).
 * Availability is resolved from the var groups already fetched by the
 * frontend — no extra API call.
 */
export const isRelatedChartAvailable = (
    definition: Readonly<RelatedChartDefinition>,
    sensorGroups: readonly Readonly<SensorGroup>[],
): boolean => {
    const stationCodes = new Set<SensorNameEnum>();
    for (const sensorGroup of sensorGroups) {
        for (const sensor of sensorGroup.sensors) {
            if (sensor.code !== null && sensor.graphable) {
                stationCodes.add(sensor.code);
            }
        }
    }

    return definition.sensorFamilies.every((family) =>
        family.some((code) => stationCodes.has(code)),
    );
};

/**
 * Resolves the station sensors and y-axes a related chart has to plot.
 *
 * Sensors sharing the same `code` across var groups are de-duplicated (the
 * same convention as the custom graph selector) and non-graphable sensors
 * are ignored. The y-axes of the contributing var groups are kept so that
 * variables the backend groups on a shared axis stay grouped, but each axis
 * is narrowed to the selected sensors; sensors not covered by any group
 * axis get their own axis downstream (StationGraphViewmodel).
 */
export const selectRelatedChartData = (
    definition: Readonly<RelatedChartDefinition>,
    sensorGroups: readonly Readonly<SensorGroup>[],
): RelatedChartSelection => {
    const chartCodes = new Set<SensorNameEnum>(
        definition.sensorFamilies.flat(),
    );

    const seenCodes = new Set<SensorNameEnum>();
    const sensors: Readonly<Sensor>[] = [];
    const yAxes: YAxes[] = [];

    for (const sensorGroup of sensorGroups) {
        const groupSensors = sensorGroup.sensors.filter(
            (sensor) =>
                sensor.code !== null &&
                sensor.graphable &&
                chartCodes.has(sensor.code) &&
                !seenCodes.has(sensor.code),
        );

        if (!groupSensors.length) continue;

        groupSensors.forEach((sensor) => {
            if (sensor.code !== null) seenCodes.add(sensor.code);
        });
        sensors.push(...groupSensors);

        const groupSensorIds = new Set(groupSensors.map((sensor) => sensor.id));
        sensorGroup.yAxes.forEach((yAxis) => {
            const variableIds = yAxis.variableIds.filter((variableId) =>
                groupSensorIds.has(variableId),
            );
            if (variableIds.length) {
                yAxes.push({
                    id: yAxes.length,
                    label: yAxis.label,
                    variableIds,
                });
            }
        });
    }

    return { sensors, yAxes };
};
